import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Search, ExternalLink, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { VirtualTable } from '@/components/ui/VirtualTable';
import { VIRTUAL_SCROLL_CONFIG } from '@/utils/virtualScrollConfig';

interface Team {
  id: string;
  name: string;
  status: string;
  created_at: string;
  member_count: number;
}

export function OrganizationManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const { data: organizations, isLoading } = useQuery({
    queryKey: ['platform-admin-organizations', searchQuery],
    queryFn: async () => {
      // Use security definer function to bypass RLS for platform admins
      const { data, error } = await supabase
        .rpc('get_all_teams_for_platform_admin');

      if (error) throw error;

      let results = data as Team[];

      // Apply client-side search filter if needed
      if (searchQuery) {
        results = results.filter(team => 
          team.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      return results;
    }
  });

  const handleViewOrganization = (orgId: string) => {
    // Navigate to organization detail page
    navigate(`/admin/organizations/${orgId}`);
  };

  const renderOrganizationRow = (org: Team, index: number) => (
    <TableRow key={org.id}>
      <TableCell className="font-medium">{org.name}</TableCell>
      <TableCell>
        <Badge variant={org.status === 'active' ? 'default' : 'secondary'}>
          {org.status}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Users className="h-4 w-4 text-muted-foreground" />
          {org.member_count}
        </div>
      </TableCell>
      <TableCell>
        {new Date(org.created_at).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleViewOrganization(org.id)}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );

  const renderTableHeader = () => (
    <TableRow>
      <TableHead>Organization Name</TableHead>
      <TableHead>Status</TableHead>
      <TableHead>Members</TableHead>
      <TableHead>Created</TableHead>
      <TableHead className="text-right">Actions</TableHead>
    </TableRow>
  );

  const shouldUseVirtualScrolling = organizations && organizations.length > VIRTUAL_SCROLL_CONFIG.ORGANIZATIONS_THRESHOLD;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Management</CardTitle>
        <CardDescription>
          View and manage all customer organizations
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading organizations...
          </div>
        ) : organizations && organizations.length > 0 ? (
          shouldUseVirtualScrolling ? (
            <VirtualTable
              items={organizations}
              renderRow={renderOrganizationRow}
              renderHeader={renderTableHeader}
              estimateSize={VIRTUAL_SCROLL_CONFIG.ESTIMATED_TABLE_ROW_HEIGHT}
              className="rounded-md border"
            />
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>{renderTableHeader()}</TableHeader>
                <TableBody>
                  {organizations.map((org, index) => renderOrganizationRow(org, index))}
                </TableBody>
              </Table>
            </div>
          )
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery ? 'No organizations found matching your search' : 'No organizations found'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
